import { useEffect, useLayoutEffect, useRef, useState, type MutableRefObject } from 'react';
import { createPortal } from 'react-dom';
import { motion, useReducedMotion } from 'framer-motion';
import Lottie, { type LottieRefCurrentProps } from 'lottie-react';
import { MOTION_PRESS_DURATION_MS } from '@/constants/motionPress';
import { PageTransitionEntranceContext } from '@/context/PageTransitionEntranceContext';
import logoTransition from '@/assets/lottie/logo_transicion.json';
import styles from './PageTransition.module.scss';
import type { PageTransitionProps } from './PageTransition.types';

type Phase = 'idle' | 'covering' | 'holding' | 'revealing';

const HOLD_DURATION_MS = 2200;
const COVER_DURATION_S = 0.48;
const REVEAL_DURATION_S = 0.48;

/**
 * PageTransition
 * Wraps content with an animated overlay that transitions on route change.
 * Overlay rises from bottom (secondary-100, lavanda claro) → holds ~2.2s → lowers to reveal new page.
 * Tras un click/tap, espera a que termine el feedback de escala del botón antes de subir el overlay.
 * Uses useLayoutEffect to detect route changes before paint, avoiding content flash.
 */
export function PageTransition({
  children,
  pathname,
  blocker,
  holdDuration = HOLD_DURATION_MS,
  coverDuration = COVER_DURATION_S,
  revealDuration = REVEAL_DURATION_S,
}: PageTransitionProps) {
  const prefersReducedMotion = useReducedMotion();
  const pressLeadMs = prefersReducedMotion ? 0 : MOTION_PRESS_DURATION_MS;

  const [phase, setPhase] = useState<Phase>('idle');
  const prevPathnameRef = useRef(pathname);
  const pathnameRef = useRef(pathname);
  const transitionStartedByBlockerRef = useRef(false);
  const navigationPressTimerRef = useRef<number | null>(null);
  const blockerPressTimerRef = useRef<number | null>(null);
  pathnameRef.current = pathname;

  const clearTimer = (ref: MutableRefObject<number | null>) => {
    if (ref.current != null) {
      window.clearTimeout(ref.current);
      ref.current = null;
    }
  };

  const clearNavigationPressTimer = () => clearTimer(navigationPressTimerRef);
  const clearBlockerPressTimer = () => clearTimer(blockerPressTimerRef);

  useEffect(
    () => () => {
      clearNavigationPressTimer();
      clearBlockerPressTimer();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Sincronizar prevPathname durante holding/revealing para no re-disparar al volver a idle
  useLayoutEffect(() => {
    if ((phase === 'holding' || phase === 'revealing') && prevPathnameRef.current !== pathname) {
      prevPathnameRef.current = pathname;
    }
  }, [pathname, phase]);

  // Si el usuario cancela la navegación bloqueada antes de que arranque el overlay, anular el delay del tap
  useLayoutEffect(() => {
    if (blocker?.state !== 'blocked') {
      clearBlockerPressTimer();
      transitionStartedByBlockerRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blocker?.state]);

  // Con blocker: la navegación se retrasa hasta que el overlay cubra — no hay flash
  useLayoutEffect(() => {
    if (blocker?.state === 'blocked' && phase === 'idle') {
      transitionStartedByBlockerRef.current = true;
      clearNavigationPressTimer();
      clearBlockerPressTimer();
      if (pressLeadMs <= 0) {
        setPhase('covering');
        return;
      }
      blockerPressTimerRef.current = window.setTimeout(() => {
        blockerPressTimerRef.current = null;
        setPhase('covering');
      }, pressLeadMs);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blocker?.state, phase, pressLeadMs]);

  // Sin bloqueo: navegación inmediata; la cortina violeta corre salvo pares en skipTransition.
  useLayoutEffect(() => {
    const notBlocked = blocker == null || blocker.state !== 'blocked';
    const notFromBlocker = !transitionStartedByBlockerRef.current;
    if (notBlocked && notFromBlocker && prevPathnameRef.current !== pathname && phase === 'idle') {
      const prev = prevPathnameRef.current;
      const fromResult = /^\/student\/result\/[^/]+$/.test(prev);
      const toResourceDetail = /^\/student\/resources\/[^/]+$/.test(pathname);
      /** Salida desde resultado → detalle Aprender: sin cortina (misma lógica histórica). */
      const skipTransition = fromResult && toResourceDetail;

      prevPathnameRef.current = pathname;

      if (skipTransition) {
        clearNavigationPressTimer();
        setPhase((p) => p);
        return;
      }

      clearNavigationPressTimer();
      clearBlockerPressTimer();
      if (pressLeadMs <= 0) {
        setPhase('covering');
        return;
      }
      navigationPressTimerRef.current = window.setTimeout(() => {
        navigationPressTimerRef.current = null;
        setPhase('covering');
      }, pressLeadMs);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, phase, blocker, pressLeadMs]);

  const handleCoverComplete = () => {
    if (blocker?.state === 'blocked') {
      blocker.proceed();
    }
    setPhase('holding');
  };

  const handleHoldComplete = () => {
    setPhase('revealing');
  };

  const handleRevealComplete = () => {
    prevPathnameRef.current = pathnameRef.current;
    transitionStartedByBlockerRef.current = false;
    setPhase('idle');
  };

  useEffect(() => {
    if (phase !== 'holding') return;

    const timer = setTimeout(handleHoldComplete, holdDuration);
    return () => clearTimeout(timer);
  }, [phase, holdDuration]);

  // Fallback: if onAnimationComplete doesn't fire during 'revealing' (Framer Motion intermittent bug),
  // force phase back to 'idle' so allowContentEntrance never stays permanently false.
  useEffect(() => {
    if (phase !== 'revealing') return;
    const fallbackMs = Math.ceil(revealDuration * 1000) + 400;
    const timer = window.setTimeout(() => {
      setPhase('idle');
    }, fallbackMs);
    return () => window.clearTimeout(timer);
  }, [phase, revealDuration]);

  // Logo animado centrado en la cortina violeta: arranca cuando el overlay queda
  // fijo (holding). En reduce-motion se muestra el logo final estático.
  const logoRef = useRef<LottieRefCurrentProps>(null);
  useEffect(() => {
    if (phase !== 'holding') return;
    const logo = logoRef.current;
    if (!logo) return;
    if (prefersReducedMotion) {
      const lastFrame = Math.max(0, (logo.getDuration(true) ?? 1) - 1);
      logo.goToAndStop(lastFrame, true);
      return;
    }
    logo.goToAndStop(0, true);
    logo.play();
  }, [phase, prefersReducedMotion]);

  const showOverlay = phase !== 'idle';
  const allowContentEntrance = phase === 'idle';

  const overlayElement = showOverlay && (
    <motion.div
      className={styles.pageTransition__overlay}
      role="presentation"
      aria-hidden="true"
      initial={phase === 'covering' ? { y: '100%' } : false}
          animate={
            phase === 'covering'
              ? { y: 0 }
              : phase === 'revealing'
                ? { y: '100%' }
                : { y: 0 }
          }
      transition={{
        duration: phase === 'covering' ? coverDuration : revealDuration,
        ease: [0.32, 0.72, 0, 1],
      }}
      onAnimationComplete={() => {
        if (phase === 'covering') {
          handleCoverComplete();
        } else if (phase === 'revealing') {
          handleRevealComplete();
        }
      }}
    >
      <div className={styles.pageTransition__logo}>
        <Lottie lottieRef={logoRef} animationData={logoTransition} loop={false} autoplay={false} />
      </div>
    </motion.div>
  );

  // Durante covering el contenido sigue visible: con blocker la ruta aún no ha cambiado,
  // así se ve la historia/conversación mientras sube la cortina; tras proceed() y en holding/revealing el resultado queda bajo el violeta opaco hasta el reveal.
  const isContentVisible = phase === 'idle' || phase === 'covering' || phase === 'revealing';

  return (
    <PageTransitionEntranceContext.Provider value={{ allowContentEntrance }}>
      <div className={styles.pageTransition}>
        <div
          className={`${styles.pageTransition__content} ${!isContentVisible ? styles['pageTransition__content--hidden'] : ''}`}
          aria-hidden={!isContentVisible}
        >
          {children}
        </div>
        {overlayElement && createPortal(overlayElement, document.body)}
      </div>
    </PageTransitionEntranceContext.Provider>
  );
}

PageTransition.displayName = 'PageTransition';
